import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { ClassSession, ClassSessionsDetailResponse } from "../../interfaces/classSessionsDetail";
import { useHttpClient } from "../../hooks/useHttpClient";
import { useLocalization } from "../../stores/localizationContext";
import { useUserContext } from "../../stores/userContext";
import { useLiveSessionStore } from "../../stores/useLiveSessionStore";
import ClassSessionsHeader from "./ClassSessionsHeader/ClassSessionsHeader";
import TutorSessionCard from "./TutorSessionCard/TutorSessionCard";
import "./ClassSessions.scss";

const ClassSessions: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const { user } = useUserContext();
  const api = useHttpClient();
  const { t, lang } = useLocalization();
  const navigate = useNavigate();
  const setSessionDurationInMinutes = useLiveSessionStore(
    (state) => state.setSessionDurationInMinutes
  );

  const [classData, setClassData] = useState<ClassSessionsDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClassSessions = async () => {
      if (!user?.userId || !classId) return;

      setLoading(true);
      setError(null);

      try {
        const endpoint = import.meta.env.VITE_GET_CLASS_SESSIONS
          .replace("{educator_id}", user.userId)
          .replace("{class_id}", classId);

        const response = await api.get(endpoint);
        setClassData(response.data);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403) {
          setError(t("unauthorized_access_error"));
        } else if (status === 404) {
          setError(t("class_not_found", "Class not found or not available."));
        } else {
          setError(t("generic_error", "Something went wrong. Please try again later."));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchClassSessions();
  }, [user?.userId, classId]);

  const handleStart = (session: ClassSession) => {
    if (session.materials && session.materials.length > 0) {
      const materialId = session.materials[0];
      setSessionDurationInMinutes(session.session_duration_in_minutes);

      if (session.materials_md5?.[materialId]) {
        sessionStorage.setItem(
          `session_${session.session_id}_material_md5`,
          session.materials_md5[materialId]
        );
      }

      navigate(`/${lang}/sessions/${session.session_id}/live/${materialId}`);
    } else {
      console.warn("No materials provided for session");
    }
  };

  const handlePreview = (session: ClassSession) => {
    console.log(`Preview clicked for session ${session.session_id}`);
  };

  const handleEndedClick = (session: ClassSession) => {
    navigate(`/${lang}/classes/${classId}/sessions/${session.session_id}`);
  };

  if (loading) {
    return (
      <div className="class-sessions__loading">
        <p dir="auto">{t("loading", "Loading...")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="class-sessions__error">
        <p dir="auto">{error}</p>
      </div>
    );
  }

  if (!classData) return null;

  const allSessions = [...classData.sessions].sort(
    (a, b) => a.session_order - b.session_order
  );

  return (
    <div className="class-sessions">
      <ClassSessionsHeader data={classData} />

      <div className="class-sessions__list">
        <div className="container">
          {allSessions.length === 0 ? (
            <p className="class-sessions__empty" dir="auto">
              {t("no_sessions_available", "No sessions available.")}
            </p>
          ) : (
            <div className="class-sessions__cards">
              {allSessions.map((session) => (
                <TutorSessionCard
                  key={session.session_id}
                  session={session}
                  currentServerDate={classData.current_server_date}
                  onStart={() => handleStart(session)}
                  onPreview={() => handlePreview(session)}
                  onEndedClick={() => handleEndedClick(session)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassSessions;
